<template>
  <select
    :value="modelValue"
    :disabled="disabled"
    :required="required"
    :class="[
      'w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue focus:border-blue transition-colors',
      disabled ? 'bg-gray-100 cursor-not-allowed text-gray-500' : 'bg-white text-gray-900',
      error ? 'border-red' : 'border-gray-300',
      selectClass
    ]"
    @change="handleChange"
  >
    <option v-if="placeholder" value="" disabled>{{ placeholder }}</option>
    <template v-for="(option, index) in normalizedOptions" :key="index">
      <optgroup v-if="option.options" :label="option.label">
        <option
          v-for="(subOption, subIndex) in option.options"
          :key="subIndex"
          :value="subOption.value"
        >
          {{ subOption.text }}
        </option>
      </optgroup>
      <option v-else :value="option.value">
        {{ option.text }}
      </option>
    </template>
  </select>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Option {
  value: string | number
  text: string
  options?: Option[]
  label?: string
}

interface Props {
  modelValue: string | number
  options: Option[] | any[]
  placeholder?: string
  disabled?: boolean
  required?: boolean
  error?: boolean
  selectClass?: string
  valueField?: string
  textField?: string
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  disabled: false,
  required: false,
  error: false,
  selectClass: '',
  valueField: 'value',
  textField: 'text',
})

const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  change: [event: Event]
}>()

const normalizedOptions = computed(() => {
  return props.options.map((option) => {
    if (typeof option === 'object' && option !== null) {
      if (option.options) {
        return {
          label: option.label,
          options: option.options.map((subOption: any) => ({
            value: subOption[props.valueField],
            text: subOption[props.textField],
          })),
        }
      }
      return {
        value: option[props.valueField],
        text: option[props.textField],
      }
    }
    return {
      value: option,
      text: option,
    }
  })
})

const handleChange = (event: Event) => {
  const target = event.target as HTMLSelectElement
  emit('update:modelValue', target.value)
  emit('change', event)
}
</script>
